import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotify } from '../../context/NotificationContext';
import { Table, Button, Input, Select, Space, Tag, Popconfirm, Tooltip, Avatar, Typography, Switch } from 'antd';
import { EditOutlined, CopyOutlined, DeleteOutlined, ReloadOutlined, StarOutlined, StarFilled } from '@ant-design/icons';
import collectionsService from '../../services/collectionsService';

const { Search } = Input;
const { Option } = Select;
const { Text } = Typography;

const AllCollectionsList = ({ activeTab }) => {
  const navigate = useNavigate();
  const notify = useNotify(); 
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
  });
  const [filters, setFilters] = useState({ status: 'all', search: '' });

  // Handler functions defined before columns
  const handleToggleFavorite = async (collectionId) => {
    try {
      await collectionsService.toggleUserPinned(collectionId);
      setRows(prevRows => 
        prevRows.map(row => 
          row.collection_id === collectionId 
            ? { ...row, rcum_is_active: !row.rcum_is_active }
            : row
        )
      );
      notify.success({ message: 'Favorites updated' });
    } catch (e) {
      notify.error({ message: 'Failed to update favorites' });
    }
  };

  const handleToggleActive = async (collectionId, isActive) => {
    try {
      await collectionsService.updateCollection(collectionId, { is_active: isActive });
      setRows(prevRows => 
        prevRows.map(row => 
          row.collection_id === collectionId 
            ? { ...row, is_active: isActive }
            : row
        )
      );
      notify.success({ message: `Collection ${isActive ? 'activated' : 'deactivated'}` });
    } catch (e) {
      notify.error({ message: 'Failed to update status' });
    }
  };

  const columns = useMemo(() => [
    {
      title: 'Collection',
      key: 'collection',
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Avatar
            size={40}
            src={record.image}
            style={{ 
              backgroundColor: '#f5f5f5',
              flexShrink: 0
            }}
          >
            {record.name?.charAt(0)?.toUpperCase()}
          </Avatar>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontWeight: 500, color: '#262626', marginBottom: 2 }}>
              {record.name}
            </div>
            <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
              {record.slug}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'Template',
      key: 'template',
      render: (_, record) => (
        <Text style={{ fontSize: '13px' }}>
          {record.template_relation?.temp_name || '-'}
        </Text>
      ),
      width: 150,
    },
    {
      title: 'Category',
      key: 'category',
      render: (_, record) => (
        <Text style={{ fontSize: '13px' }}>
          {record.categories_relation?.vc_name || '-'}
        </Text>
      ),
      width: 150,
    },
    {
      title: 'Type',
      key: 'type',
      render: (_, record) => (
        <Tag 
          color={record.is_manual ? 'blue' : 'default'}
          style={{ fontSize: '11px', margin: 0 }}
        >
          {record.is_manual ? 'Manual' : 'Auto'}
        </Tag>
      ),
      width: 80,
      align: 'center',
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => (
        <Space direction="vertical" size={2} style={{ width: '100%' }}>
          <Switch
            checked={record.is_active}
            onChange={(checked) => handleToggleActive(record.collection_id, checked)}
            checkedChildren="Active"
            unCheckedChildren="Inactive"
            style={{ width: 90 }}
          />
        </Space>
      ),
      width: 110,
      align: 'center',
    },
    {
      title: 'Order',
      dataIndex: 'order_no',
      key: 'order_no',
      width: 80,
      align: 'center',
      render: (v) => <Text style={{ fontSize: '13px' }}>{v ?? '-'}</Text>,
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 120,
      render: (_, record) => (
        <Space size={2}>
          <Tooltip title={record.rcum_is_active ? "Remove from favorites" : "Add to favorites"}>
            <Button 
              icon={record.rcum_is_active ? <StarFilled /> : <StarOutlined />} 
              size="small" 
              type="text"
              onClick={() => handleToggleFavorite(record.collection_id)}
              style={{ 
                color: record.rcum_is_active ? '#faad14' : '#d9d9d9'
              }}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button 
              icon={<EditOutlined />} 
              size="small" 
              type="text" 
              onClick={() => navigate(`/collections/edit/${record.collection_id}`)}
              style={{ color: '#1890ff' }}
            />
          </Tooltip>
          <Tooltip title="Duplicate">
            <Button 
              icon={<CopyOutlined />} 
              size="small" 
              type="text" 
              onClick={() => handleDuplicate(record.collection_id)}
              style={{ color: '#52c41a' }}
            />
          </Tooltip>
          <Popconfirm 
            title="Delete collection?" 
            onConfirm={() => handleDelete(record.collection_id)} 
            okText="Yes" 
            cancelText="No"
          >
            <Button 
              icon={<DeleteOutlined />} 
              size="small" 
              type="text" 
              danger
            />
          </Popconfirm>
        </Space>
      ),
    },
  ], []);

  const getData = async () => {
    try {
      setLoading(true);
      const res = await collectionsService.getCollections({
        page: pagination.current,
        limit: pagination.pageSize,
        status: filters.status,
        search: filters.search,
      });
      
      if (res?.data?.status) {
        setRows(res.data.data || []);
        const meta = res.data.meta || {};
        setPagination((p) => ({ ...p, total: meta.totalEntries || 0, current: meta.currentPage || 1 }));
      }
    } catch (e) {
      notify.error({ message: 'Failed to load collections' });
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicate = async (id) => {
    try {
      await collectionsService.duplicateCollection(id);
      notify.success({ message: 'Collection duplicated' });
      getData();
    } catch (e) {
      notify.error({ message: 'Failed to duplicate' });
    }
  };

  const handleDelete = async (id) => {
    try {
      await collectionsService.deleteCollection(id);
      notify.success({ message: 'Collection deleted' });
      getData();
    } catch (e) {
      notify.error({ message: 'Failed to delete' });
    }
  };

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize, filters.status, filters.search,activeTab]);

  return (
    <div>
      {/* Simple Header Controls */}
      <div style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <Search
          placeholder="Search collections..."
          allowClear
          value={filters.search}
          onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
          onSearch={() => getData()}
          style={{ width: 280 }}
        />
        <Select
          style={{ width: 140 }}
          value={filters.status}
          onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
        >
          <Option value="all">All Status</Option>
          <Option value="active">Active</Option>
          <Option value="inactive">Inactive</Option>
        </Select>
        <div style={{ flex: 1 }} />
        <Text type="secondary" style={{ fontSize: '13px' }}>
          {pagination.total} collections
        </Text>
        <Button 
          icon={<ReloadOutlined />} 
          onClick={getData}
          type="text"
          size="small"
        />
      </div>

      {/* Clean Collections Table */}
      <Table
        rowKey="collection_id"
        columns={columns}
        dataSource={rows}
        loading={loading}
        pagination={pagination}
        onChange={(pg) => setPagination((p) => ({ ...p, current: pg.current, pageSize: pg.pageSize }))}
        scroll={{ x: 800 }}
        size="middle"
        style={{ 
          backgroundColor: '#fff',
          borderRadius: 8,
          overflow: 'hidden',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}
      />
    </div>
  );
};

export default AllCollectionsList;
